(ns hai.home.mobile.add-sheet
  "Floating + button and the bottom Add sheet for the mobile dashboard.

   The task tile opens the hai.web-app.add-menu task dialog — mount
   [[hai.web-app.add-menu/modals]] alongside this component. The note and
   contact tiles open their full-screen editors ([[hai.notes.create/overlay]]
   and [[hai.contacts.create/overlay]], mounted at the app root)."
  (:require
   [hai.contacts.create :as contact-create]
   [hai.home.mobile.state :as state]
   [hai.notes.create :as note-create]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.add-menu :as add-menu]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private plus-icon (icons/mui-icon :outlined :add))

;; ---------------------------------------------------------------------------
;; Styled pieces
;; ---------------------------------------------------------------------------

(o/defstyled scrim :div
  {:position "fixed" :inset "0"
   :background "rgba(31,45,32,0.4)"
   :z-index "90"
   :opacity "0"
   :pointer-events "none"
   :transition "opacity 240ms ease"}
  [:&.open {:opacity "1" :pointer-events "auto"}])

(o/defstyled fab :button
  {:position "fixed"
   :bottom "28px" :right "18px"
   :width "56px" :height "56px"
   :border-radius "999px"
   :border "none"
   :background "linear-gradient(135deg, #F4D06F 0%, #E0A648 100%)"
   :color "#2D3E2A"
   :cursor "pointer"
   :box-shadow "0 8px 20px rgba(31,45,32,0.20), 0 2px 4px rgba(31,45,32,0.14)"
   :z-index "110"
   :display "inline-flex" :align-items "center" :justify-content "center"
   :transition "transform 180ms cubic-bezier(.2,.7,.3,1), box-shadow 180ms ease"}
  [:&:hover
   {:box-shadow "0 10px 24px rgba(31,45,32,0.26), 0 2px 4px rgba(31,45,32,0.14)"}]
  [:&:active {:transform "scale(0.96)"}]
  [:&.open {:transform "rotate(45deg)"}]
  [:&.open:active {:transform "rotate(45deg) scale(0.96)"}]
  [:svg {:width "26px" :height "26px" :fill "currentColor"}])

(o/defstyled sheet :div
  {:position "fixed"
   :left "0" :right "0" :bottom "0"
   :background "#F7F6EF"
   :border-radius "28px 28px 0 0"
   :box-shadow "0 -12px 36px rgba(31,45,32,0.18)"
   :z-index "100"
   :transform "translateY(100%)"
   :transition "transform 320ms cubic-bezier(.2,.7,.3,1)"
   :padding "8px 20px 28px"
   :display "flex" :flex-direction "column" :gap "14px"}
  [:&.open {:transform "translateY(0)"}])

(o/defstyled sheet-grab :div
  {:height "4px" :width "44px"
   :background "#D9E0CE"
   :border-radius "999px"
   :margin "0 auto 6px"
   :flex "0 0 auto"})

(o/defstyled sheet-title :h2
  {:font-size "22px" :font-weight "700"
   :letter-spacing "-0.01em"
   :color "#2D3E2A"
   :margin "0"})

(o/defstyled tile-grid :div
  {:display "grid"
   :grid-template-columns "1fr 1fr"
   :gap "12px"})

(o/defstyled tile :button
  {:display "flex" :align-items "center" :gap "12px"
   :padding "14px"
   :border "1px solid #D9E0CE"
   :background "#FFFFFF"
   :border-radius "16px"
   :cursor "pointer"
   :font-family "inherit"
   :text-align "left"
   :color "#2D3E2A"
   :transition "transform 0.15s ease, box-shadow 0.15s ease"}
  [:&:hover
   {:transform "translateY(-1px)"
    :box-shadow "0 4px 14px rgba(45,62,42,0.10)"}]
  [:&:active {:transform "translateY(0)"}]
  [:.swatch
   {:width "36px" :height "36px"
    :border-radius "10px"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :flex "0 0 auto"}
   [:svg {:width "20px" :height "20px" :fill "currentColor"}]]
  [:.swatch.task {:background "#C0CFA8" :color "#2D3E2A"}]
  [:.swatch.note {:background "#F5E7A8" :color "#5B4A1F"}]
  [:.swatch.contact {:background t/--contact :color "#2D3E2A"}]
  [:.label-stack
   {:display "flex" :flex-direction "column" :min-width "0" :gap "2px"}]
  [:.label
   {:font-size "14px" :font-weight "700" :color "#2D3E2A"}]
  [:.sublabel
   {:font-size "11px" :color "#6B7560"}])

;; ---------------------------------------------------------------------------
;; Logic
;; ---------------------------------------------------------------------------

(defn- open! [] (>e [::state/set-add-sheet-open? true]))
(defn- close! [] (>e [::state/set-add-sheet-open? false]))

(defn- pick!
  "Close the sheet and open the picked kind's creation surface."
  [open-event]
  (close!)
  (>e [open-event]))

(defn fab+sheet []
  (r/with-let [on-keyup (fn [e]
                          (when (= "Escape" (.-key e))
                            (close!)))
               _ (.addEventListener js/window "keyup" on-keyup)]
    (let [open? @(<s [::state/add-sheet-open?])]
      [:<>
       [scrim {:class       (when open? "open")
               :on-click    close!
               :aria-hidden "true"}]
       [sheet
        {:class       (when open? "open")
         :role        "dialog"
         :aria-modal  "true"
         :aria-hidden (if open? "false" "true")
         :aria-label  "Add"}
        [sheet-grab]
        [sheet-title "Add"]
        [tile-grid
         [tile {:type     "button"
                :on-click #(pick! ::add-menu/open-task-modal)}
          [:span.swatch.task [icons/humble-task]]
          [:div.label-stack
           [:span.label "Task"]
           [:span.sublabel "with link, details"]]]
         [tile {:type     "button"
                :on-click #(pick! ::note-create/open)}
          [:span.swatch.note [icons/humble-note]]
          [:div.label-stack
           [:span.label "Note"]
           [:span.sublabel "quick thought"]]]
         [tile {:type     "button"
                :on-click #(pick! ::contact-create/open)}
          [:span.swatch.contact [icons/outlined-people]]
          [:div.label-stack
           [:span.label "Contact"]
           [:span.sublabel "person or organisation"]]]]]
       [fab
        {:type       "button"
         :aria-label (if open? "Close add menu" "Add")
         :class      (when open? "open")
         :on-click   (if open? close! open!)}
        [plus-icon]]])
    (finally
      (.removeEventListener js/window "keyup" on-keyup))))
